import React from "react";
import { FileText } from "lucide-react";

export const LabVisits = ({ visits, expandedVisit, visitDetail, toggleVisit }) => (
  <section className="px-6 sm:px-12 lg:px-20 pb-12 max-w-6xl mx-auto">
    <div className="flex items-center justify-between mb-4">
      <p className="label-eyebrow">Lab visits</p>
      {visits.length > 0 && <span className="text-xs text-[#5C6359]" data-testid="visits-count">{visits.length} on file</span>}
    </div>
    {visits.length === 0 ? (
      <p className="text-sm text-[#5C6359] bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6" data-testid="no-visits">
        Upload a lab PDF/image above and each report becomes a single visit card here.
      </p>
    ) : (
      <div className="space-y-3" data-testid="visits-list">
        {visits.map((v) => {
          const isOpen = expandedVisit === v.id;
          const dateLabel = v.date ? new Date(v.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "Undated";
          return (
            <div key={v.id} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl overflow-hidden" data-testid={`visit-card-${v.id}`}>
              <button
                onClick={() => toggleVisit(v.id)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-[#EAE3D9] transition-colors"
                data-testid={`visit-toggle-${v.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#2D322B] text-[#D49A44] flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-[#2D322B] leading-tight">
                      {v.name || "Lab visit"} · <span className="text-[#5C6359]">{dateLabel}</span>
                    </p>
                    <p className="text-xs text-[#5C6359] mt-1">
                      {v.marker_count} markers
                      {v.attention_count > 0 && (
                        <span className="ml-2 text-[#8F3A3A]">· {v.attention_count} need attention</span>
                      )}
                      {v.sample_markers?.length > 0 && (
                        <span className="ml-2 text-[#5C6359]/80">· {v.sample_markers.slice(0, 4).join(", ")}{v.marker_count > 4 ? "…" : ""}</span>
                      )}
                    </p>
                  </div>
                </div>
                <span className={`text-xs uppercase tracking-widest text-[#C05C42] transition-transform ${isOpen ? "rotate-180" : ""}`}>▾</span>
              </button>
              {isOpen && (
                <div className="border-t border-[#D1C7B8] p-5 bg-[#F9F6F0]" data-testid={`visit-detail-${v.id}`}>
                  {!visitDetail ? (
                    <p className="text-sm text-[#5C6359]">Loading…</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-widest text-[#5C6359]">
                        <tr>
                          <th className="text-left pb-2">Marker</th>
                          <th className="text-left pb-2 w-28">Value</th>
                          <th className="text-left pb-2 w-32">Zone</th>
                          <th className="text-left pb-2">Optimal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitDetail.entries.map((e) => {
                          const z = e.zone;
                          const tone = z?.status === "optimal"
                            ? "text-[#2A3B2C]"
                            : z?.status === "attention"
                              ? "text-[#8F3A3A]"
                              : z?.status === "borderline"
                                ? "text-[#8a6620]"
                                : "text-[#5C6359]";
                          return (
                            <tr key={e.id} className="border-t border-[#D1C7B8]">
                              <td className="py-2 pr-3 font-serif text-[#2D322B]">{e.marker}</td>
                              <td className="py-2 pr-3 text-[#C05C42]">{e.value} {e.unit}</td>
                              <td className={`py-2 pr-3 text-xs uppercase tracking-widest ${tone}`}>{z?.zone || "—"}</td>
                              <td className="py-2 text-xs text-[#5C6359]">{z ? `${z.optimal_low}–${z.optimal_high} ${z.unit}` : "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </section>
);
